import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Player, TableAssignment } from '@/types';

interface PlayersState {
  players: Player[];
  selectedPlayers: string[];
  filter: {
    showActive: boolean;
    showEliminated: boolean;
    showUnpaid: boolean;
  };
}

const initialState: PlayersState = {
  players: [],
  selectedPlayers: [],
  filter: {
    showActive: true,
    showEliminated: true,
    showUnpaid: true,
  },
};

const playersSlice = createSlice({
  name: 'players',
  initialState,
  reducers: {
    addPlayer: (state, action: PayloadAction<Omit<Player, 'id'>>) => {
      const newPlayer: Player = {
        ...action.payload,
        id: crypto.randomUUID(),
      };
      state.players.push(newPlayer);
    },
    
    addPlayers: (state, action: PayloadAction<Omit<Player, 'id'>[]>) => {
      const newPlayers = action.payload.map(player => ({
        ...player,
        id: crypto.randomUUID(),
      }));
      state.players.push(...newPlayers);
    },
    
    updatePlayer: (state, action: PayloadAction<{ id: string; updates: Partial<Player> }>) => {
      const index = state.players.findIndex(p => p.id === action.payload.id);
      if (index !== -1) {
        state.players[index] = { ...state.players[index], ...action.payload.updates };
      }
    },
    
    deletePlayer: (state, action: PayloadAction<string>) => {
      state.players = state.players.filter(p => p.id !== action.payload);
      state.selectedPlayers = state.selectedPlayers.filter(id => id !== action.payload);
    },
    
    deleteAllPlayers: (state) => {
      state.players = [];
      state.selectedPlayers = [];
    },
    
    eliminatePlayer: (state, action: PayloadAction<{ id: string; round: number }>) => {
      const index = state.players.findIndex(p => p.id === action.payload.id);
      if (index !== -1) {
        state.players[index].timeOut = new Date();
        state.players[index].roundOut = action.payload.round;
      }
    },
    
    reinstatePlayer: (state, action: PayloadAction<string>) => {
      const index = state.players.findIndex(p => p.id === action.payload);
      if (index !== -1) {
        state.players[index].timeOut = undefined;
        state.players[index].roundOut = 0;
      }
    },
    
    addRebuy: (state, action: PayloadAction<string>) => {
      const index = state.players.findIndex(p => p.id === action.payload);
      if (index !== -1) {
        state.players[index].rebuys += 1;
      }
    },
    
    addAddOn: (state, action: PayloadAction<string>) => {
      const index = state.players.findIndex(p => p.id === action.payload);
      if (index !== -1) {
        state.players[index].addOns += 1;
      }
    },
    
    setPaid: (state, action: PayloadAction<{ id: string; paid: boolean }>) => {
      const index = state.players.findIndex(p => p.id === action.payload.id);
      if (index !== -1) {
        state.players[index].paid = action.payload.paid;
      }
    },
    
    setAllPaid: (state, action: PayloadAction<boolean>) => {
      state.players.forEach(player => {
        player.paid = action.payload;
      });
    },
    
    assignSeats: (state, action: PayloadAction<TableAssignment[]>) => {
      action.payload.forEach(assignment => {
        const index = state.players.findIndex(p => p.id === assignment.playerId);
        if (index !== -1) {
          state.players[index].table = assignment.table;
          state.players[index].seat = assignment.seat;
        }
      });
    },
    
    movePlayer: (state, action: PayloadAction<{ id: string; table: number; seat: number }>) => {
      const index = state.players.findIndex(p => p.id === action.payload.id);
      if (index !== -1) {
        state.players[index].table = action.payload.table;
        state.players[index].seat = action.payload.seat;
        state.players[index].numberOfTableMoves += 1;
      }
    },
    
    updateRankings: (state, action: PayloadAction<{ id: string; rank: number }[]>) => {
      action.payload.forEach(({ id, rank }) => {
        const index = state.players.findIndex(p => p.id === id);
        if (index !== -1) {
          state.players[index].rank = rank;
        }
      });
    },
    
    selectPlayer: (state, action: PayloadAction<string>) => {
      if (!state.selectedPlayers.includes(action.payload)) {
        state.selectedPlayers.push(action.payload);
      }
    },
    
    deselectPlayer: (state, action: PayloadAction<string>) => {
      state.selectedPlayers = state.selectedPlayers.filter(id => id !== action.payload);
    },
    
    togglePlayerSelection: (state, action: PayloadAction<string>) => {
      const index = state.selectedPlayers.indexOf(action.payload);
      if (index === -1) {
        state.selectedPlayers.push(action.payload);
      } else {
        state.selectedPlayers.splice(index, 1);
      }
    },
    
    selectAllPlayers: (state) => {
      state.selectedPlayers = state.players.map(p => p.id);
    },
    
    deselectAllPlayers: (state) => {
      state.selectedPlayers = [];
    },
    
    setFilter: (state, action: PayloadAction<Partial<PlayersState['filter']>>) => {
      state.filter = { ...state.filter, ...action.payload };
    },
    
    loadPlayers: (state, action: PayloadAction<Player[]>) => {
      state.players = action.payload;
      state.selectedPlayers = [];
    },
    
    resetPlayers: (state) => {
      return { ...initialState };
    },
  },
});

export const {
  addPlayer,
  addPlayers,
  updatePlayer,
  deletePlayer,
  deleteAllPlayers,
  eliminatePlayer,
  reinstatePlayer,
  addRebuy,
  addAddOn,
  setPaid,
  setAllPaid,
  assignSeats,
  movePlayer,
  updateRankings,
  selectPlayer,
  deselectPlayer,
  togglePlayerSelection,
  selectAllPlayers,
  deselectAllPlayers,
  setFilter,
  loadPlayers,
  resetPlayers,
} = playersSlice.actions;

export default playersSlice.reducer;